///
/// EarthBrowser
///
/// Created by Matt Giger on 10/30/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Which satellites are on the globe, as a strip of toggle chips under the
/// Satellites row in the Layers panel: one chip per curated set
/// (satelliteSets.ts) with its orbit-tint dot and object count, heavy sets
/// (thousands of objects) counting in amber. Collapsed, the row shows the
/// selected sets' dots and the total so the state reads without opening
/// the strip. Counts come from the local TLE table and follow the layer's
/// refresh, so they are exact for this catalog, not CelesTrak's nominal
/// group sizes.
///

import { SatelliteTLETable } from "@/earthbrowser/dataset-distribution/satellites/SatelliteTLETable"
import { cSatelliteSets, groupIdsForSets, type SatelliteSet } from "@/earthbrowser/dataset-distribution/satellites/satelliteSets"
import { useDatasetLayerRefresh } from "@/hooks/useDatasetLayerRefresh"
import { toggleSatelliteSet } from "@/redux/earthBrowserSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { cn } from "@/utils/utils"
import { useEffect, useMemo, useState } from "react"

// Sets at or above this many objects are flagged as heavy on the chip
const cHeavyCount = 2000

const cOrbitDot: Record<SatelliteSet["orbit"], string> = {
	LEO: "bg-cyan-400",
	MEO: "bg-blue-400",
	GEO: "bg-amber-400",
	mixed: "bg-cyan-400",
}

function dotClass(set: SatelliteSet): string {
	return set.id === "debris" ? "bg-slate-400" : cOrbitDot[set.orbit]
}

function formatCount(n: number): string {
	if (n >= 10000) return `${Math.round(n / 1000)}k`
	if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}k`
	return String(n)
}

/**
 * Object counts per set from the local catalog, re-read whenever the
 * satellite layer refreshes (sync landed, sets changed)
 */
function useSatelliteSetCounts(): Record<string, number> {
	const [counts, setCounts] = useState<Record<string, number>>({})
	const [version, setVersion] = useState(0)
	useDatasetLayerRefresh("satellites", () => setVersion(v => v + 1))

	useEffect(() => {
		let cancelled = false
		const load = async () => {
			const next: Record<string, number> = {}
			for (const set of cSatelliteSets) {
				try {
					next[set.id] = await SatelliteTLETable.getCountByGroupIds(set.groupIds)
				} catch {
					// Table not ready yet (another tab owns the DB, or first sync pending)
				}
			}
			if (!cancelled) setCounts(next)
		}
		void load()
		return () => {
			cancelled = true
		}
	}, [version])

	return counts
}

/** Objects on the globe for the current selection (union, not a sum) */
function useSelectedSatelliteCount(selected: readonly string[]): number | null {
	const [count, setCount] = useState<number | null>(null)
	const [version, setVersion] = useState(0)
	useDatasetLayerRefresh("satellites", () => setVersion(v => v + 1))
	const groupKey = useMemo(() => groupIdsForSets(selected).join("|"), [selected])

	useEffect(() => {
		let cancelled = false
		const groupIds = groupKey ? groupKey.split("|") : []
		SatelliteTLETable.getCountByGroupIds(groupIds)
			.then(n => {
				if (!cancelled) setCount(n)
			})
			.catch(() => {
				if (!cancelled) setCount(null)
			})
		return () => {
			cancelled = true
		}
	}, [groupKey, version])

	return count
}

interface SatelliteSetChipStripProps {
	isMobile: boolean
}

export function SatelliteSetChipStrip({ isMobile }: SatelliteSetChipStripProps) {
	const dispatch = useAppDispatch()
	const selected = useAppSelector(state => state.earthbrowser.satelliteSets)
	const counts = useSatelliteSetCounts()

	return (
		<div
			role="group"
			aria-label="Satellite sets on the globe"
			// Indented under the parent's name (disclosure + icon columns)
			className={cn("flex flex-wrap gap-1 pb-2 pr-2", isMobile ? "pl-9 pt-1" : "pl-[38px] pt-0.5")}
		>
			{cSatelliteSets.map(set => {
				const on = selected.includes(set.id)
				const count = counts[set.id]
				const heavy = set.heavy || (count !== undefined && count >= cHeavyCount)
				const countLabel = count !== undefined ? formatCount(count) : null
				return (
					<button
						key={set.id}
						type="button"
						onClick={e => {
							e.stopPropagation()
							dispatch(toggleSatelliteSet(set.id))
						}}
						aria-pressed={on}
						title={heavy
							? `${set.name}: ${count?.toLocaleString() ?? "thousands of"} objects, heavy on slower devices`
							: `${set.name}${count !== undefined ? `: ${count.toLocaleString()} objects` : ""}`}
						className={cn(
							"inline-flex items-center gap-1.5 rounded-full border font-medium transition-colors",
							"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/50",
							isMobile ? "h-7 px-2.5 text-xs" : "h-[22px] px-2 text-[11px]",
							on
								? "border-cyan-500/50 bg-cyan-500/15 text-cyan-200 hover:bg-cyan-500/25"
								: "border-white/15 text-slate-300 hover:border-white/30 hover:text-white hover:bg-white/5"
						)}
					>
						<span
							className={cn("h-1.5 w-1.5 rounded-full flex-shrink-0", on ? dotClass(set) : "bg-slate-500")}
							aria-hidden="true"
						/>
						{set.short}
						{countLabel && (
							<span className={cn(
								"font-normal tabular-nums",
								heavy && on ? "text-amber-300" : on ? "text-cyan-300/60" : "text-slate-500"
							)}>
								{countLabel}
							</span>
						)}
					</button>
				)
			})}
		</div>
	)
}

/**
 * Collapsed-row summary: a dot per selected set (up to four, then "+N")
 * and the number of objects they put on the globe. Kept to a few
 * characters so the layer name never gives way to it; the set names are
 * in the tooltip
 */
export function SatelliteSetSummary() {
	const selected = useAppSelector(state => state.earthbrowser.satelliteSets)
	const total = useSelectedSatelliteCount(selected)
	const chosen = cSatelliteSets.filter(set => selected.includes(set.id))

	if (chosen.length === 0) {
		return <span className="text-[11px] text-slate-500 whitespace-nowrap">no sets</span>
	}

	const names = chosen.map(set => set.name).join(", ")
	return (
		<span
			className="inline-flex items-center gap-1 text-[11px] text-slate-500 whitespace-nowrap"
			title={`${names}${total !== null ? ` · ${total.toLocaleString()} objects` : ""}`}
			aria-label={`${chosen.length} satellite ${chosen.length === 1 ? "set" : "sets"}: ${names}`}
		>
			<span className="inline-flex items-center gap-0.5" aria-hidden="true">
				{chosen.slice(0, 4).map(set => (
					<span key={set.id} className={cn("h-1.5 w-1.5 rounded-full", dotClass(set))} />
				))}
				{chosen.length > 4 && <span className="ml-0.5">+{chosen.length - 4}</span>}
			</span>
			{total !== null && <span className="tabular-nums">{formatCount(total)}</span>}
		</span>
	)
}
